import React, { useCallback, useEffect, useState } from "react";

import { popupService } from "api/services/marketing.service";
import ContentHeader from "components/Layout/ContentHeader";
import CreatePopUpModal from "components/marketing/CreatePopUpModal";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import Table, { Column } from "components/UI/Table";
import Toggle from "components/UI/Toggle";

import type { PopupItem, PopupLinkType, PopupPosition } from "api/types/marketing.types";
import "pages/AdminPage.scss";
import "./PopupManagement.scss";

const LINK_TYPE_LABEL: Record<PopupLinkType, string> = {
  NONE: "없음",
  INTERNAL: "내부",
  EXTERNAL: "외부",
};

const POSITION_LABEL: Record<PopupPosition, string> = {
  CENTER: "중앙",
  TOP: "상단",
  BOTTOM: "하단",
};

type ExpireStatus = "active" | "expired" | "scheduled";

const getExpireStatus = (startDate?: string | null, endDate?: string | null): ExpireStatus => {
  if (!startDate || !endDate) return "active";

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  if (today > end) return "expired";
  if (today < start) return "scheduled";
  return "active";
};

const EXPIRE_BADGE: Record<ExpireStatus, { label: string; variant: "active" | "rejected" | "pending" }> = {
  active: { label: "공개", variant: "active" },
  expired: { label: "만료", variant: "rejected" },
  scheduled: { label: "예정", variant: "pending" },
};

const PopupManagement: React.FC = () => {
  const [popups, setPopups] = useState<PopupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<PopupItem | null>(null);

  const fetchPopups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await popupService.list();
      setPopups(res.data.results);
    } catch {
      setError("팝업 목록을 불러오는 데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPopups();
  }, [fetchPopups]);

  const openCreate = () => {
    setEditTarget(null);
    setShowModal(true);
  };

  const openEdit = (popup: PopupItem) => {
    setEditTarget(popup);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditTarget(null);
  };

  const handleSuccess = () => {
    closeModal();
    fetchPopups();
  };

  const handleDelete = async (popup: PopupItem) => {
    if (!window.confirm(`"${popup.title}" 팝업을 삭제하시겠습니까?`)) return;
    try {
      await popupService.remove(popup.id);
      fetchPopups();
    } catch {
      alert("삭제에 실패했습니다.");
    }
  };

  const handleToggleActive = async (id: number, isActive: boolean) => {
    try {
      await popupService.toggleActive(id, isActive);
      fetchPopups();
    } catch {
      alert("상태 변경에 실패했습니다.");
    }
  };

  const columns: Column<PopupItem>[] = [
    { key: "title", title: "팝업명" },
    {
      key: "image_url",
      title: "이미지",
      width: "80px",
      render: (value: string | null, row: PopupItem) => (value ? <img className="popup-management__thumbnail" src={value} alt={row.alt_text || row.title} /> : <span className="popup-management__no-image">-</span>),
    },
    {
      key: "link_type",
      title: "링크유형",
      // width: "90px",
      render: (value: PopupLinkType) => LINK_TYPE_LABEL[value] ?? value,
    },
    {
      key: "position",
      title: "위치",
      // width: "80px",
      render: (value: PopupPosition) => POSITION_LABEL[value] ?? value,
    },
    { key: "priority", title: "우선순위", width: "80px" },
    {
      key: "start_date",
      title: "기간",
      width: "200px",
      render: (_: string, row: PopupItem) => `${row.start_date} ~ ${row.end_date}`,
    },
    {
      key: "is_active",
      title: "활성화",
      render: (_, row) => <Toggle checked={row.is_active} onChange={() => handleToggleActive(row.id, !row.is_active)} />,
    },
    {
      key: "is_expire",
      title: "상태",
      render: (_, row) => {
        const { label, variant } = EXPIRE_BADGE[getExpireStatus(row.start_date, row.end_date)];
        return <Badge variant={variant}>{label}</Badge>;
      },
    },
    {
      key: "actions",
      title: "",
      render: (_, row: PopupItem) => (
        <div className="popup-management__actions" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="outline" onClick={() => handleDelete(row)}>
            삭제
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="팝업 관리"
        description="앱 내 팝업 노출을 설정합니다."
        actions={[
          <Button key="add" variant="primary" onClick={openCreate}>
            + 팝업 생성
          </Button>,
        ]}
      />

      {error && (
        <div className="admin-page__card popup-management__error">
          <p>{error}</p>
          <Button variant="secondary" onClick={fetchPopups}>
            다시 시도
          </Button>
        </div>
      )}

      {!error && <div className="admin-page__card">{loading ? <div className="popup-management__loading">데이터를 불러오는 중입니다...</div> : <Table columns={columns} data={popups} rowKey="id" onRowClick={openEdit} />}</div>}

      <CreatePopUpModal open={showModal} editTarget={editTarget} onClose={closeModal} onSuccess={handleSuccess} />
    </div>
  );
};

export default PopupManagement;
